import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import SourceEvidencePanel from '../../components/SourceEvidencePanel';
import { TREND_COLORS } from '../../trendTheme';
import { useTrendAdmin } from '../TrendAdminContext';
import { sectionTitle } from '../adminUi';
import TrendEmptyState from '../../components/TrendEmptyState';
import {
  brandRadarClassificationActionLabel,
  brandRadarClassificationColor,
  brandRadarClassificationLabel,
  brandRadarSentimentLabel,
} from '../adminHelpers';

const BRAND_RADAR_ACCENT: Record<string, string> = {
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
  info: '#0288d1',
  default: TREND_COLORS.divider,
};

export default function BrandRadarTab() {
  const {
    brandRadarHits,
    brandRadarQueriesDraft,
    setBrandRadarQueriesDraft,
    busyAction,
    hasActiveRun,
    handleStartBrandRadar,
    showBrandRadarFalsePositives,
    setShowBrandRadarFalsePositives,
    handleUpdateBrandRadarHit,
  } = useTrendAdmin();

  const visibleHits = brandRadarHits;
  const riskCount = visibleHits.filter((hit) => hit.classification === 'risk' && !hit.isFalsePositive).length;
  const opportunityCount = visibleHits.filter((hit) => hit.classification === 'opportunity' && !hit.isFalsePositive).length;
  const falsePositiveCount = visibleHits.filter((hit) => hit.isFalsePositive).length;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        {sectionTitle('Brand Radar', 'Ett separat spår för Fontana-träffar: sentiment, risk/möjlighet/ingen åtgärd, falska träffar och originalkälla.')}
        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mb: 2 }}>
          Brand Radar kör en smal AI-webbsökning/discovery för Fontana-termerna och klassar sedan både nya discoveryträffar och befintligt underlag.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 2 }}>
          <Box>
            <TextField
              multiline
              minRows={5}
              fullWidth
              label="Fontana-sökningar"
              helperText="En sökning per rad eller separera med kommatecken."
              value={brandRadarQueriesDraft}
              onChange={(event) => setBrandRadarQueriesDraft(event.target.value)}
            />
          </Box>
          <Box>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`${visibleHits.length} träffar`} />
                <Chip color="error" label={`${riskCount} risk`} />
                <Chip color="success" label={`${opportunityCount} möjlighet`} />
                <Chip variant="outlined" label={`${falsePositiveCount} falska träffar`} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={busyAction === 'brandRadarRun' || hasActiveRun}
                  onClick={() => void handleStartBrandRadar()}
                  sx={{ bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } }}
                >
                  Sök nya Brand Radar-träffar
                </Button>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Switch
                    checked={showBrandRadarFalsePositives}
                    onChange={(event) => setShowBrandRadarFalsePositives(event.target.checked)}
                  />
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
                    Visa även irrelevanta träffar
                  </Typography>
                </Stack>
              </Stack>
              {hasActiveRun && (
                <Alert severity="info" sx={{ py: 0 }}>
                  En trendkörning pågår redan. Starta Brand Radar när den är klar.
                </Alert>
              )}
            </Stack>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        {sectionTitle('Senaste Brand Radar-träffar', 'Granska träffar, markera irrelevanta träffar och justera om de ska hanteras som risk, möjlighet eller ingen åtgärd.')}
        {visibleHits.length === 0 ? (
          <TrendEmptyState
            dense
            title="Inga Brand Radar-träffar ännu"
            description="Starta ett Brand Radar-test ovan för att hämta omnämnanden av Fontana och konkurrenter."
          />
        ) : (
          <Stack spacing={2}>
            {visibleHits.map((hit) => (
              <Box
                key={hit._id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: hit.isFalsePositive ? '#fff8e1' : TREND_COLORS.sectionBg,
                  border: `1px solid ${TREND_COLORS.divider}`,
                  borderLeft: `3px solid ${BRAND_RADAR_ACCENT[brandRadarClassificationColor(hit.classification)] || TREND_COLORS.divider}`,
                  opacity: hit.isFalsePositive ? 0.75 : 1,
                  transition: 'box-shadow 0.15s ease',
                  '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.06)' },
                }}
              >
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                        {hit.title || 'Brand Radar-träff utan rubrik'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        {[hit.matchedTerm, brandRadarSentimentLabel(hit.sentiment), `${hit.severityScore} prioritet`, hit.publishedAt?.slice(0, 10)].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ justifyContent: 'flex-end' }}>
                      <Chip size="small" color={brandRadarClassificationColor(hit.classification)} label={brandRadarClassificationLabel(hit.classification)} />
                      {hit.isFalsePositive && <Chip size="small" color="warning" label="Falsk träff" />}
                    </Stack>
                  </Stack>
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
                    {hit.snippet || 'Ingen sammanfattning sparad.'}
                  </Typography>
                  {hit.url && (
                    <Link href={hit.url} target="_blank" rel="noopener noreferrer" underline="hover" variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                      Öppna originalkälla
                    </Link>
                  )}
                  {hit.falsePositiveReason && (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      {hit.falsePositiveReason}
                    </Alert>
                  )}
                  <SourceEvidencePanel refs={hit.sourceEvidenceRefs} dense title="Källunderlag" />
                  <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                    Välj hur träffen ska hanteras internt. Markera som irrelevant om den inte gäller Fontana Food eller om träffen är brus.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      variant={hit.isFalsePositive ? 'contained' : 'outlined'}
                      color="warning"
                      disabled={busyAction === 'brandRadarReview'}
                      onClick={() => void handleUpdateBrandRadarHit(hit._id, {
                        isFalsePositive: !hit.isFalsePositive,
                        falsePositiveReason: !hit.isFalsePositive ? 'Markerad som falsk träff manuellt.' : '',
                      })}
                    >
                      {hit.isFalsePositive ? 'Ångra irrelevant' : 'Markera som irrelevant'}
                    </Button>
                    {(['risk', 'opportunity', 'no_action'] as const).map((classification) => (
                      <Button
                        key={`${hit._id}-${classification}`}
                        size="small"
                        variant={hit.classification === classification ? 'contained' : 'outlined'}
                        disabled={busyAction === 'brandRadarReview'}
                        onClick={() => void handleUpdateBrandRadarHit(hit._id, { classification })}
                        sx={hit.classification === classification ? { bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } } : undefined}
                      >
                        {brandRadarClassificationActionLabel(classification)}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
